/**
 * History Data Adapter - Provides data source abstraction for history page
 *
 * Allows the history page to display data from different sources:
 * - Local database (user's own data)
 * - Remote API (monitored user's data)
 */

import { getDb, dbManager } from "../db/user-database";
import type { CgmReading, LogEntry, UserSettings, DayNote } from "../types";
import { syncService } from "./sync/sync-service";

export interface HistoryData {
  readings: CgmReading[];
  entries: LogEntry[];
  settings?: UserSettings;
  dayNotes: DayNote[];
}

export interface HistoryDataAdapter {
  /** Load data from the source */
  loadData(): Promise<HistoryData>;

  /** Optional: Title to display */
  title?: string;

  /** Optional: Subtitle to display */
  subtitle?: string;

  /** Optional: Back button handler - if provided, shows back button */
  onBack?: () => void;

  /** Optional: Save a note - if provided, allows adding notes in the view */
  saveNote?(date: string, content: string): Promise<{ noteId: string }>;

  /** Whether this adapter supports adding notes */
  canAddNotes?: boolean;
}

/**
 * Local adapter - loads data from IndexedDB (user's own data)
 */
export class LocalHistoryAdapter implements HistoryDataAdapter {
  async loadData(): Promise<HistoryData> {
    if (!dbManager.isInitialized) {
      return { readings: [], entries: [], dayNotes: [] };
    }
    const db = getDb();

    // Load core data first - dayNotes might not exist on older DB versions
    const [readings, entries, settings] = await Promise.all([
      db.cgmReadings.toArray(),
      db.entries.toArray(),
      db.settings.get("settings"),
    ]);

    // Try to load dayNotes - table might not exist yet
    let dayNotes: DayNote[] = [];
    try {
      if (db.dayNotes) {
        const notes = await db.dayNotes.toArray();
        dayNotes = (notes || []).filter((n) => !n._deletedAt) as DayNote[];
      }
    } catch (e) {
      console.warn("dayNotes table not available yet:", e);
    }

    console.log(
      `LocalHistoryAdapter: Loaded ${readings?.length || 0} readings, ${entries?.length || 0} entries`,
    );

    return {
      readings: (readings || []).filter((r) => !r._deletedAt) as CgmReading[],
      entries: (entries || []).filter((e) => !e._deletedAt) as LogEntry[],
      settings: settings as UserSettings | undefined,
      dayNotes,
    };
  }
}

/**
 * Monitored user adapter - loads data from API for a user being monitored
 */
export class MonitoredUserAdapter implements HistoryDataAdapter {
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  canAddNotes = true; // Clinicians can add notes to patient data
  private userId: string;

  constructor(
    userId: string,
    displayName: string,
    email?: string,
    onBack?: () => void,
  ) {
    this.userId = userId;
    this.title = displayName;
    this.subtitle = email;
    this.onBack = onBack;
  }

  async loadData(): Promise<HistoryData> {
    const data = await syncService.fetchMonitoredUserData(this.userId);

    if (!data) {
      throw new Error("Unable to access data. You may not have permission.");
    }

    // Get settings from the response (first item in array)
    const settingsArray = data.settings as unknown[];
    const settings =
      settingsArray && settingsArray.length > 0
        ? (settingsArray[0] as UserSettings)
        : undefined;

    return {
      readings: (data.cgmReadings || []) as CgmReading[],
      entries: (data.entries || []) as LogEntry[],
      settings,
      dayNotes: (data.dayNotes || []) as DayNote[],
    };
  }

  async saveNote(date: string, content: string): Promise<{ noteId: string }> {
    const result = await syncService.addMonitoredUserNote(
      this.userId,
      date,
      content,
    );
    if (!result) {
      throw new Error("Failed to save note");
    }
    return { noteId: result.noteId };
  }
}
